import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const fallbackArticle = articles[0];
  const article = articles.find((a) => a.id === id) || fallbackArticle;
  const relatedArticles = articles.filter((a) => a.id !== id).slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">文章不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const fullContent = article.content || generateContent(article);

  return (
    <>
      <Navbar />

      <main>
        {/* Breadcrumb */}
        <div className="bg-background-50 pt-24 pb-4">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Hero */}
        <section className="pt-8 pb-12 md:pt-12 md:pb-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="lg:w-2/5">
                <span className="inline-block px-3 py-1 text-xs font-semibold bg-accent-500 text-foreground-950 rounded-full mb-4 whitespace-nowrap">
                  {article.category}
                </span>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-black text-foreground-900 leading-tight mb-4">
                  {article.title.replace('某', '<span class="italic text-primary-500">某</span>')}
                </h1>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                  {article.excerpt}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400 mb-6">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.readCount > 10000
                      ? `${(article.readCount / 10000).toFixed(1)}万`
                      : article.readCount}{' '}
                    阅读
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs bg-background-100 text-foreground-500 rounded-full whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="lg:w-3/5">
                <img
                  src={article.image}
                  alt={article.title}
                  title={`${article.title} - 52吃瓜网`}
                  className="w-full rounded-3xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-12 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="bg-background-50 rounded-2xl p-6 md:p-10 lg:p-12">
              <div className="prose prose-lg max-w-none">
                <div
                  className="text-foreground-700 leading-relaxed space-y-6 text-base md:text-lg"
                  dangerouslySetInnerHTML={{ __html: fullContent }}
                />
              </div>

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 text-xs bg-secondary-100 text-secondary-800 rounded-full whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-12">
              <div>
                <p className="text-sm text-foreground-400 font-medium tracking-wide mb-2">
                  猜你想看
                </p>
                <h2 className="text-2xl md:text-4xl font-heading font-black text-foreground-900">
                  相关<span className="italic text-primary-500 font-heading">推荐</span>
                </h2>
              </div>
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((rel, idx) => (
                <ArticleCard
                  key={rel.id}
                  article={rel}
                  variant={idx === 1 ? 'featured' : 'default'}
                />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative w-full min-h-[400px] md:min-h-[480px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20abstract%20illustration%20of%20notification%20bell%20and%20breaking%20news%20alerts%2C%20modern%20editorial%20digital%20art%20with%20soft%20gradients%2C%20coral%20and%20amber%20tones%2C%20stay%20updated%20concept%2C%20cozy%20evening%20vibe%2C%20newsletter%20subscription%20theme%2C%20friendly%20and%20inviting%20atmosphere&width=1800&height=700&seq=article-cta-bg&orientation=landscape"
              alt="关注52吃瓜网"
              title="关注更多爆料"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-foreground-900/55"></div>
          </div>
          <div className="relative w-full max-w-2xl mx-auto px-4 md:px-6 text-center py-16">
            <h2 className="text-2xl md:text-4xl font-heading font-black text-background-50 leading-tight mb-4">
              不想错过下一个大瓜？
            </h2>
            <p className="text-sm md:text-base text-background-200 leading-relaxed mb-8">
              关注52吃瓜网，第一时间获取最新爆料推送
            </p>
            <a
              href="#download"
              className="inline-flex items-center gap-3 px-8 py-4 bg-foreground-950 text-background-50 font-semibold text-sm rounded-full hover:bg-foreground-900 transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center shrink-0">
                <i className="ri-notification-3-line text-foreground-950 text-sm"></i>
              </span>
              开启推送通知
              <i className="ri-arrow-right-up-line ml-1"></i>
            </a>
          </div>
        </section>
      </main>

      <Footer />

      {/* Schema.org */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.image,
            datePublished: article.date,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            publisher: {
              '@type': 'Organization',
              name: '52吃瓜网',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `http://rdstudio.cn/article/${article.id}`,
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'http://rdstudio.cn/' },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.category,
                  item: `http://rdstudio.cn/category/${article.categorySlug}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                  item: `http://rdstudio.cn/article/${article.id}`,
                },
              ],
            },
          }),
        }}
      />
    </>
  );
}

function generateContent(article: typeof articles[0]): string {
  const paragraphs = [
    `<p>近日，一则关于<strong>${article.title.replace(/某/g, '')}</strong>的消息在网络上引起了广泛关注。据知情人士透露，该事件的相关细节正在被逐步披露，引发了广大网友的热烈讨论。</p>`,
    `<p>根据多方渠道获得的信息显示，本次事件的起因可以追溯到${article.date}之前。有消息人士向记者透露，当事方目前尚未对此事做出正面回应，但已经在私下展开了紧急公关处理。</p>`,
    `<h2 class="text-xl font-heading font-bold text-foreground-900 mt-8 mb-4">事件背景</h2>`,
    `<p>了解整件事情的来龙去脉，需要从更早的时间节点说起。早在去年年底，就有眼尖的网友发现了一些端倪。当时，有人在社交媒体上发布了一组对比图，引发了小范围的猜测和讨论。不过由于缺乏实质性证据，当时并未引起太大的波澜。</p>`,
    `<p>然而，随着时间推移，越来越多的线索开始浮出水面。多位自称知情的网友先后在各大社交平台上发布了相关爆料，每一条信息都像拼图一样，将整个事件的面貌逐渐拼凑完整。</p>`,
    `<h2 class="text-xl font-heading font-bold text-foreground-900 mt-8 mb-4">多方反应</h2>`,
    `<p>事件持续发酵后，各方反应不一。一些相关人士选择了沉默，拒绝接受任何形式的采访。而另一些则选择了主动发声，试图澄清事实或将舆论引导至有利方向。</p>`,
    `<p>值得关注的是，不少业内人士也纷纷发表了自己的看法。有资深媒体人分析认为，此次事件的走向将取决于后续是否有更多实质性证据被公开。如果只是一些捕风捉影的传闻，随着时间的推移自然会平息。</p>`,
    `<p>但也有观点认为，考虑到当前舆论场的情绪，即便没有更多实锤证据，本次事件也很可能会对当事人的公众形象产生难以挽回的负面影响。</p>`,
    `<h2 class="text-xl font-heading font-bold text-foreground-900 mt-8 mb-4">网友热议</h2>`,
    `<p>在各大社交平台上，相关话题的热度持续攀升。截至目前，#${article.tags[0]}#话题的阅读量已经突破1亿。网友们纷纷在评论区发表看法，讨论热情高涨。</p>`,
    `<p>有网友评论道："这样的瓜请多来一点，每天最大的快乐就是看这些了。"也有理性的声音表示："吃瓜归吃瓜，还是要保持理性，不要被带节奏。"</p>`,
    `<p>还有不少网友将注意力转向了事件相关方的过往历史，挖掘出了不少有趣的"彩蛋"内容，进一步丰富了整个事件的讨论维度。</p>`,
    `<h2 class="text-xl font-heading font-bold text-foreground-900 mt-8 mb-4">后续发展预测</h2>`,
    `<p>根据目前的态势判断，该事件在短期内不太可能迅速降温。除非当事方能够拿出足够有力的证据来澄清所有质疑，否则舆论的焦点仍将持续集中在此事上。</p>`,
    `<p>值得关注的是，随着事件热度的持续发酵，是否会牵扯出更多相关人物和事件，也是众多吃瓜群众所期待的。毕竟在娱乐圈和网红圈，"拔出萝卜带出泥"的情况并不少见。</p>`,
    `<p>52吃瓜网将持续关注本次事件的最新进展，第一时间为各位吃瓜群众带来最新的爆料和深度分析。敬请关注我们的后续报道。</p>`,
    `<p class="text-sm text-foreground-400 mt-8"><em>声明：本文内容来源于网友投稿和网络爆料，52吃瓜网仅做收集整理，不对内容真实性做任何保证。如有不实信息，请联系我们删除。</em></p>`,
  ];

  return paragraphs.join('\n');
}